import { useCallback, useEffect } from "react";
import { Timetable, type TimetableProps } from "./Timetable";
import { checkoutOverlayMounted } from "~/widgets/lib/overlay-hosts";

export type TimetableOverlayProps = Omit<
  TimetableProps,
  "mode" | "buttonText" | "buttonClassName" | "buttonStyle"
>;

export function TimetableOverlay(props: TimetableOverlayProps) {
  const handleClose = useCallback(() => {
    const event = new CustomEvent("baluu:timetable-close", {
      bubbles: true,
      composed: true,
    });
    document.dispatchEvent(event);

    const overlays = document.querySelectorAll("baluu-timetable-overlay");
    overlays.forEach((overlay) => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });
  }, []);

  useEffect(() => {
    // Not an Escape a dialog over the overlay already took (Radix prevents
    // its default): the basket drawer, which opens above this overlay —
    // closing it must leave the timetable where it was. Nor while a checkout
    // opened from it sits above it: the checkouts have no Escape, and closing
    // the timetable would take that checkout down with it.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !checkoutOverlayMounted()) handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [handleClose]);

  return (
    <div className="baluu-widget-root fixed inset-0 flex flex-col bg-gray-50">
      <div className="flex items-center justify-between gap-3 px-4 h-12 bg-white border-b border-gray-200 shrink-0">
        <span className="text-sm font-medium text-gray-900">Timetable</span>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close timetable"
          className="inline-flex items-center justify-center w-9 h-9 -mr-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="flex-1 min-h-0">
        <Timetable {...props} mode="inline" />
      </div>
    </div>
  );
}
